/* useSeo.js — injects title + meta + open-graph tags from
 * /api/seo/by-route?route=<path> into <head> while a page is mounted.
 *
 * Usage:
 *   useSeo({
 *     route: "/",
 *     fallback: { title: "Sunday Digital", description: "…" },
 *   });
 *
 * The hook is intentionally side-effect-only: it never re-renders the
 * component. We update tags imperatively and restore the previous title
 * on unmount so route transitions stay clean.
 */
import { useEffect } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.match || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs.set || {}).forEach(([k, v]) => {
    if (v == null || v === "") el.removeAttribute(k);
    else el.setAttribute(k, v);
  });
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const applySeo = (seo) => {
  if (!seo) return;
  if (seo.title) document.title = seo.title;
  upsertMeta('meta[name="description"]', {
    match: { name: "description" },
    set: { content: seo.description || "" },
  });
  upsertMeta('meta[name="robots"]', {
    match: { name: "robots" },
    set: { content: seo.noindex ? "noindex, nofollow" : "index, follow" },
  });
  // Open Graph
  upsertMeta('meta[property="og:title"]', {
    match: { property: "og:title" },
    set: { content: seo.og_title || seo.title || "" },
  });
  upsertMeta('meta[property="og:description"]', {
    match: { property: "og:description" },
    set: { content: seo.og_description || seo.description || "" },
  });
  upsertMeta('meta[property="og:image"]', {
    match: { property: "og:image" },
    set: { content: seo.og_image || "" },
  });
  upsertMeta('meta[property="og:url"]', {
    match: { property: "og:url" },
    set: { content: seo.canonical || (typeof window !== "undefined" ? window.location.href : "") },
  });
  // Twitter
  upsertMeta('meta[name="twitter:card"]', {
    match: { name: "twitter:card" },
    set: { content: seo.og_image ? "summary_large_image" : "summary" },
  });
  upsertLink("canonical", seo.canonical || "");
};

export function useSeo({ route, fallback = {} } = {}) {
  useEffect(() => {
    if (!route) return undefined;
    const prevTitle = document.title;
    let cancelled = false;

    // Apply fallback immediately so even a slow API gives the user a title
    applySeo(fallback);

    fetch(`${BASE}/api/seo/by-route?route=${encodeURIComponent(route)}`, {
      credentials: "omit",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((doc) => {
        if (cancelled || !doc) return;
        // Merge: API values override fallback when present, else keep fallback
        const merged = { ...fallback };
        Object.keys(doc).forEach((k) => {
          if (doc[k] !== null && doc[k] !== "") merged[k] = doc[k];
        });
        applySeo(merged);
      })
      .catch(() => {
        // Fallback already applied
      });

    return () => {
      cancelled = true;
      document.title = prevTitle;
    };
  }, [route]);
}
